import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const GinkgoLeaf = () => (
  <svg width="28" height="28" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M50 90 C50 90 20 65 20 40 C20 22 33 10 50 10 C67 10 80 22 80 40 C80 65 50 90 50 90Z" fill="#2C4A3B" opacity="0.8"/>
    <path d="M50 90 C50 90 35 68 35 45 C35 30 41 18 50 18 C59 18 65 30 65 45 C65 68 50 90 50 90Z" fill="#C5A065" opacity="0.6"/>
    <line x1="50" y1="90" x2="50" y2="15" stroke="#F9F7F2" strokeWidth="1.5" opacity="0.7"/>
  </svg>
);

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('ginkgo_lang', lng);
  };

  const isHome = location.pathname === '/';

  const navLinks = [
    { path: '/', key: 'home' },
    { path: '/galerie', key: 'gallery' },
    { path: '/boutique', key: 'boutique' },
    { path: '/commandes', key: 'orders' },
    { path: '/contact', key: 'contact' },
    { path: '/emploi', key: 'emploi' },
  ];

  const textColor = isHome && !scrolled ? 'text-white' : 'text-ginkgo-800';
  const logoColor = isHome && !scrolled ? 'text-white' : 'text-ginkgo-800';
  const linkColor = isHome && !scrolled
    ? 'text-white/90 hover:text-white'
    : 'text-stone-600 hover:text-ginkgo-800';
  const activeLinkColor = isHome && !scrolled ? 'text-white' : 'text-ginkgo-800';
  const langActive = isHome && !scrolled ? 'text-white font-bold' : 'text-ginkgo-800 font-bold';
  const langInactive = isHome && !scrolled ? 'text-white/50 hover:text-white/80' : 'text-stone-400 hover:text-ginkgo-800';

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'bg-white/90 backdrop-blur-md border-b border-stone-200/80 shadow-sm' : 'bg-transparent'
        }`}
      >
        <nav className="max-w-[1400px] mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
            <GinkgoLeaf />
            <span className={`font-serif text-base md:text-lg font-medium tracking-tight transition-colors ${logoColor}`}>
              Les Noces de Ginkgo
            </span>
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navLinks.map(({ path, key }) => {
              const isActive = location.pathname === path;
              return (
                <Link
                  key={path}
                  to={path}
                  data-testid={`nav-link-${key}`}
                  className={`text-xs uppercase tracking-widest font-sans font-medium transition-colors duration-200 ${
                    isActive ? `${activeLinkColor} underline decoration-gold decoration-1 underline-offset-4` : linkColor
                  }`}
                >
                  {t(`nav.${key}`)}
                </Link>
              );
            })}
          </div>

          {/* Right section */}
          <div className="flex items-center gap-3">
            {/* Language switcher */}
            <div className="flex items-center gap-1" role="group" aria-label="Language selector">
              {['fr', 'en', 'de'].map((lang) => (
                <button
                  key={lang}
                  data-testid={`lang-${lang}`}
                  onClick={() => changeLanguage(lang)}
                  className={`text-xs uppercase font-medium px-2 py-1 rounded transition-colors duration-200 ${
                    i18n.language === lang ? langActive : langInactive
                  }`}
                >
                  {lang}
                </button>
              ))}
            </div>

            {/* Mobile hamburger */}
            <button
              data-testid="mobile-menu-toggle"
              className={`lg:hidden transition-colors ${textColor}`}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="fixed top-16 left-0 right-0 z-40 bg-white/97 backdrop-blur-md border-b border-stone-200 shadow-lg"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {navLinks.map(({ path, key }) => (
                <Link
                  key={path}
                  to={path}
                  data-testid={`mobile-nav-${key}`}
                  className={`text-sm uppercase tracking-widest font-medium py-3 border-b border-stone-100 last:border-0 transition-colors ${
                    location.pathname === path ? 'text-ginkgo-800' : 'text-stone-600'
                  }`}
                >
                  {t(`nav.${key}`)}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
